import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-purple-600 text-white shadow hover:bg-purple-800',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input text-gray-200 bg-transparent shadow-sm hover:bg-gray-400/20 hover:text-white',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'text-gray-400 hover:bg-white/20 hover:text-white',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  loadingWidth?: number;
  dynamicLoader?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading,
      loadingText,
      loadingWidth,
      dynamicLoader,
      ...props
    },
    ref,
  ) => {
    const [buttonWidth, setButtonWidth] = React.useState<number | null>(null);

    const buttonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
      if (buttonRef.current && buttonWidth === null) {
        setButtonWidth(buttonRef.current.offsetWidth);
      }
    }, [buttonRef, buttonWidth]);

    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={
          dynamicLoader
            ? {
                width: `${
                  loadingWidth ? loadingWidth + 'px' : buttonWidth ? buttonWidth + 'px' : 'auto'
                }`,
              }
            : undefined
        }
        ref={dynamicLoader ? buttonRef : ref}
        {...props}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export interface LinkButtonProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {
  disabled?: boolean;
}

const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ className, variant, size, href, ...props }, ref) => {
    return (
      <Link
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        href={href || ''}
        {...props}
      />
    );
  },
);
LinkButton.displayName = 'LinkButton';

export { LinkButton, Button, buttonVariants };
